import React, { useCallback, useEffect, useState } from "react";
import { HardDrive, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@pgs-core/ui/components/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@pgs-core/ui/components/select";
import { Badge } from "@pgs-core/ui/components/badge";
import type { ApiClient } from "@pgs-core/portal-lib/api";
import { formatBytes, formatTimestamp } from "@pgs-core/portal-lib/format";
import type { AdminClient } from "../lib/outlet-context";

interface BackupInfo {
  key: string;
  size: number;
  uploaded: string | null;
}

export function BackupPanel({ clients, api }: { clients: AdminClient[]; api: ApiClient | null }) {
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState<BackupInfo[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const loadBackups = useCallback(async (tenantId: string) => {
    if (!api) return;
    setBackups(null);
    setListError(null);
    try {
      const result = await api.request<{ backups: BackupInfo[] }>(`/admin/backup/${tenantId}`);
      setBackups(result?.backups ?? []);
    } catch (err) {
      setListError((err as Error).message);
      setBackups([]);
    }
  }, [api]);

  useEffect(() => {
    if (selectedId) void loadBackups(selectedId);
  }, [selectedId, loadBackups]);

  const runBackup = async () => {
    if (!selectedId || !api) return;
    setLoading(true);
    setStatus(null);
    try {
      const result = await api.request<{ file?: string }>(`/admin/backup/${selectedId}`, {
        method: "POST",
      });
      setStatus({ type: "success", message: `Backup completed. File: ${result?.file ?? "backup.sql"}` });
      await loadBackups(selectedId);
    } catch (err) {
      setStatus({ type: "error", message: (err as Error).message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Export DB to R2</CardTitle>
        <CardDescription>
          Runs a D1 SQL export and writes a timestamped .sql file to the db-backups R2 bucket
          under the tenant's prefix. A nightly job backs up every provisioned database
          automatically; dumps are kept for 30 days.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
            <p className="text-sm text-muted-foreground">No clients to back up</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Provision a client first.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-2">
                <label className="text-sm font-medium">Select client</label>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={runBackup} disabled={!selectedId || !api || loading}>
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Exporting...</>
                ) : (
                  <><HardDrive className="h-4 w-4" /> Export backup</>
                )}
              </Button>
            </div>

            {!api && (
              <p className="text-sm text-muted-foreground">
                Backup requires a configured API. Set PUBLIC_API_BASE_URL to enable.
              </p>
            )}

            {status && (
              <div className="flex items-center gap-2">
                {status.type === "success" ? (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {status.message}
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    {status.message}
                  </Badge>
                )}
              </div>
            )}

            {selectedId && api && <BackupList backups={backups} error={listError} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BackupList({ backups, error }: { backups: BackupInfo[] | null; error: string | null }) {
  if (error) {
    return <p className="text-sm text-destructive">Could not load backups: {error}</p>;
  }
  if (backups === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading backups...
      </p>
    );
  }
  if (backups.length === 0) {
    return <p className="text-sm text-muted-foreground">No backups stored for this client yet.</p>;
  }
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        Stored backups
        <span className="ml-2 font-normal text-muted-foreground">
          latest {formatTimestamp(backups[0].uploaded)}
        </span>
      </p>
      <div className="divide-y rounded-lg border">
        {backups.map((backup) => (
          <div key={backup.key} className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="truncate font-mono text-xs">{backup.key}</span>
            <span className="ml-3 shrink-0 text-muted-foreground">
              {formatTimestamp(backup.uploaded)} · {formatBytes(backup.size)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
